import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { REACTION_EMOJIS, ReactionTargetType } from '@onesource/schemas';
import { useToggleReaction } from '@onesource/services';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useReactionTouchPicker } from './use-reaction-touch-picker';

const REACTIONS = [
  { type: 'like', emoji: REACTION_EMOJIS.like, label: 'Like' },
  { type: 'love', emoji: REACTION_EMOJIS.love, label: 'Love' },
  { type: 'haha', emoji: REACTION_EMOJIS.haha, label: 'Haha' },
  { type: 'wow', emoji: REACTION_EMOJIS.wow, label: 'Wow' },
  { type: 'sad', emoji: REACTION_EMOJIS.sad, label: 'Sad' },
  { type: 'angry', emoji: REACTION_EMOJIS.angry, label: 'Angry' },
] as const;

interface ReactionPickerProps {
  targetType: 'post' | 'comment';
  targetId: string;
  currentReaction: string | null | undefined;
  likeCount: number;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  color: string;
  shape: string;
  rotation: number;
  scale: number;
}

export const ReactionPicker = ({ targetType, targetId, currentReaction, likeCount }: ReactionPickerProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [animateEmoji, setAnimateEmoji] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleReaction = useToggleReaction();
  const theme = useTheme();

  const particleColors = useMemo(() => {
    const c = theme.palette.primary.main;
    return [c, `${c}88`, `${c}66`, `${c}44`];
  }, [theme.palette.primary.main]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Close picker when tapping/clicking outside the wrapper (mobile + desktop)
  useEffect(() => {
    if (!showPicker) {
      return;
    }
    const handleOutside = (e: TouchEvent | MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('touchstart', handleOutside, { passive: true });
    document.addEventListener('mousedown', handleOutside);
    return () => {
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('mousedown', handleOutside);
    };
  }, [showPicker]);

  const createParticles = useCallback(() => {
    const shapes = ['star', 'plus', 'triangle'];
    const newParticles: Particle[] = [];

    for (let i = 0; i < 8; i++) {
      const isTopRow = i < 4;
      const baseX = (i % 4) * 20 + 5;
      const baseY = isTopRow ? -5 : 40;

      newParticles.push({
        id: Date.now() + i,
        x: baseX + (Math.random() - 0.5) * 15 - 15,
        y: baseY + (Math.random() - 0.5) * 10,
        color: particleColors[Math.floor(Math.random() * particleColors.length)],
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        rotation: Math.random() * 360,
        scale: 0.6 + Math.random() * 0.4,
      });
    }

    setParticles(newParticles);
  }, [particleColors]);

  const getParticleClipPath = useCallback((shape: string) => {
    switch (shape) {
      case 'star': {
        return 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)';
      }
      case 'plus': {
        return 'polygon(40% 0%, 60% 0%, 60% 40%, 100% 40%, 100% 60%, 60% 60%, 60% 100%, 40% 100%, 40% 60%, 0% 60%, 0% 40%, 40% 40%)';
      }
      case 'triangle': {
        return 'polygon(50% 0%, 0% 100%, 100% 100%)';
      }
      default: {
        return 'none';
      }
    }
  }, []);

  const handleReaction = useCallback(
    (emoji: string) => {
      const isNewReaction = currentReaction !== emoji;

      if (isNewReaction) {
        createParticles();
        setAnimateEmoji(true);
        setTimeout(() => setAnimateEmoji(false), 600);
      }

      toggleReaction.mutate({
        targetType: targetType === 'post' ? ReactionTargetType.POST : ReactionTargetType.COMMENT,
        targetId,
        emoji,
        currentReaction: currentReaction ?? undefined,
      });
      setShowPicker(false);
    },
    [createParticles, currentReaction, targetId, targetType, toggleReaction],
  );

  // Mobile interaction: long-press to open, drag to highlight, release to select.
  const { hoveredEmoji, isPostTouch, consumeLongPress, touchHandlers } = useReactionTouchPicker({
    onLongPressOpen: () => setShowPicker(true),
    onSelect: handleReaction,
  });

  const handleMouseEnter = () => {
    // Ignore the synthetic mouseenter that fires right after a tap on touch devices.
    if (isPostTouch()) {
      return;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setShowPicker(true);
  };

  const handleMouseLeave = () => {
    if (isPostTouch()) {
      return;
    }
    hideTimeoutRef.current = setTimeout(() => setShowPicker(false), 150);
  };

  const handleMainButtonClick = () => {
    if (consumeLongPress()) {
      return;
    }
    handleReaction(displayEmoji);
  };

  const handlePickerEmojiClick = (e: React.MouseEvent, emoji: string) => {
    e.stopPropagation();
    if (consumeLongPress()) {
      return;
    }
    handleReaction(emoji);
  };

  // Stop the count tap from bubbling to the main button (which would toggle
  // the reaction). The reactors list moved out of here into ReactorsAvatarStack.
  const handleCountClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    consumeLongPress();
  };

  // Find the current reaction emoji for display, or use thumbs up as default icon
  const displayEmoji = currentReaction || REACTION_EMOJIS.like;
  const isReacted = !!currentReaction;

  return (
    <Box
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...touchHandlers}
      sx={{ position: 'relative', display: 'inline-flex', touchAction: 'manipulation' }}
    >
      {/* Main reaction button */}
      <Box
        onClick={handleMainButtonClick}
        sx={{
          position: 'relative',
          display: 'flex',
          gap: '6px',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            lineHeight: 1,
            animation: animateEmoji ? 'reactionBeat 0.6s ease' : 'none',
            transition: 'transform 0.4s',
            '&:hover': {
              transform: 'scale(1.35)',
            },
          }}
        >
          {displayEmoji}
        </Typography>

        {/* Particle burst effect */}
        {particles.map(particle => (
          <div
            key={particle.id}
            style={{
              position: 'absolute',
              width: '8px',
              height: '8px',
              backgroundColor: particle.color,
              clipPath: getParticleClipPath(particle.shape),
              pointerEvents: 'none',
              left: `${particle.x}px`,
              top: `${particle.y}px`,
              transform: `rotate(${particle.rotation}deg) scale(${particle.scale})`,
              animation: 'reactionFloatUp 1.2s ease-out forwards',
            }}
          />
        ))}

        {/* Reaction count next to the emoji. The reactors list rendered to
            the right of the row (see ReactorsAvatarStack) — keep this purely
            informational and stop bubble so taps don't toggle the reaction. */}
        <Box
          onClick={handleCountClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'default',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Figtree, sans-serif',
              fontWeight: isReacted ? 700 : 400,
              fontSize: { xs: 9, md: 9 },
              color: isReacted ? theme.palette.primary.main : '#333',
            }}
          >
            {likeCount}
          </Typography>
        </Box>
      </Box>

      {/* Hover popup with all reactions */}
      {showPicker && (
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'white',
            borderRadius: '25px',
            boxShadow: '0 4px 18px rgba(0,0,0,0.12)',
            p: '6px 10px',
            display: 'flex',
            gap: '2px',
            zIndex: 1000,
            animation: 'reactionPickerSlideUp 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          {REACTIONS.map((r, idx) => {
            const isTouchHovered = hoveredEmoji === r.emoji;
            return (
              <Box
                key={r.type}
                data-reaction-emoji={r.emoji}
                onClick={e => handlePickerEmojiClick(e, r.emoji)}
                sx={{
                  cursor: 'pointer',
                  p: '4px',
                  borderRadius: '50%',
                  bgcolor:
                    currentReaction === r.emoji
                      ? 'rgba(255, 7, 59, 0.1)'
                      : isTouchHovered
                        ? 'rgba(0,0,0,0.05)'
                        : 'transparent',
                  transform: isTouchHovered ? 'scale(1.35) translateY(-4px)' : 'none',
                  transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  animation: `reactionEmojiPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) ${idx * 0.04}s both`,
                  '&:hover': {
                    transform: 'scale(1.35) translateY(-4px)',
                    bgcolor: 'rgba(0,0,0,0.05)',
                  },
                }}
                title={r.label}
              >
                <Typography sx={{ fontSize: 24, lineHeight: 1 }}>{r.emoji}</Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Keyframe animations */}
      <style>{`
        @keyframes reactionBeat {
          0% { transform: scale(1); }
          20% { transform: scale(1.4); }
          40% { transform: scale(1.1); }
          60% { transform: scale(1.3); }
          80% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        @keyframes reactionFloatUp {
          0% {
            opacity: 1;
            transform: translateY(0) scale(0) rotate(0deg);
          }
          15% {
            transform: translateY(-10px) scale(1) rotate(90deg);
          }
          100% {
            opacity: 0;
            transform: translateY(-60px) scale(0.2) rotate(360deg);
          }
        }

        @keyframes reactionPickerSlideUp {
          0% {
            opacity: 0;
            transform: translateX(-50%) translateY(8px) scale(0.9);
          }
          100% {
            opacity: 1;
            transform: translateX(-50%) translateY(0) scale(1);
          }
        }

        @keyframes reactionEmojiPop {
          0% {
            opacity: 0;
            transform: scale(0);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </Box>
  );
};
